import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  listProjectFotos,
  resolveProjectFotoPreviewUrl,
  type ProjectFoto,
  type ProjectFotoSession,
} from "@/lib/projectOverview";
import { DOCUMENT_EDITOR_NESTED_OVERLAY_Z } from "@/pages/dokumente/documentEditorLayers";

interface DocumentPositionProjectFotoPickerProps {
  open: boolean;
  auftragId: number;
  onClose: () => void;
  onConfirm: (fotos: ProjectFoto[]) => Promise<void> | void;
  zIndexClass?: string;
}

export function DocumentPositionProjectFotoPicker({
  open,
  auftragId,
  onClose,
  onConfirm,
  zIndexClass = DOCUMENT_EDITOR_NESTED_OVERLAY_Z,
}: DocumentPositionProjectFotoPickerProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ProjectFotoSession[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listProjectFotos(auftragId);
      setSessions(list);
    } catch (err) {
      setSessions([]);
      setError(err instanceof ApiError ? err.message : "Projektfotos konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [auftragId]);

  useEffect(() => {
    if (!open) {
      setSelectedIds(new Set());
      setError(null);
      setSaving(false);
      return;
    }
    void reload();
  }, [open, reload]);

  const imageFotos = useMemo(() => {
    const out: Array<{ session: ProjectFotoSession; foto: ProjectFoto }> = [];
    for (const session of sessions) {
      for (const foto of session.photos || []) {
        if (foto.is_image === false) continue;
        const ct = (foto.content_type || "").toLowerCase();
        if (ct && !ct.startsWith("image/")) continue;
        out.push({ session, foto });
      }
    }
    return out;
  }, [sessions]);

  function toggleFoto(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleConfirm() {
    const selected = imageFotos
      .filter((item) => selectedIds.has(item.foto.id))
      .map((item) => item.foto);
    if (!selected.length) return;
    setSaving(true);
    setError(null);
    try {
      await onConfirm(selected);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Übernahme fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Bilder aus dem Projekt"
      description="Bereits hinterlegte Projektfotos für diese Position übernehmen."
      size="xl"
      plainBackdrop
      zIndexClass={zIndexClass}
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            type="button"
            variant="primary"
            loading={saving}
            disabled={saving || selectedIds.size === 0}
            onClick={() => void handleConfirm()}
          >
            {selectedIds.size ? `${selectedIds.size} übernehmen` : "Übernehmen"}
          </Button>
        </div>
      }
    >
      {error ? (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-label-secondary">
          <Spinner className="h-4 w-4" />
          Projektfotos werden geladen …
        </div>
      ) : imageFotos.length === 0 ? (
        <p className="py-6 text-sm text-label-secondary">
          Im Projekt sind noch keine Bilder hinterlegt. Lade zuerst Projektfotos hoch oder wähle
          „Bilder vom PC“.
        </p>
      ) : (
        <div className="space-y-4">
          {sessions.map((session) => {
            const photos = (session.photos || []).filter((foto) => {
              if (foto.is_image === false) return false;
              const ct = (foto.content_type || "").toLowerCase();
              return !ct || ct.startsWith("image/");
            });
            if (!photos.length) return null;
            return (
              <section key={session.id} className="space-y-2">
                <h4 className="text-sm font-semibold text-label">
                  {session.title || "Projektfotos"}
                  <span className="ml-2 font-normal text-label-tertiary">
                    {photos.length} Bild{photos.length === 1 ? "" : "er"}
                  </span>
                </h4>
                <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                  {photos.map((foto) => {
                    const checked = selectedIds.has(foto.id);
                    const preview = resolveProjectFotoPreviewUrl(auftragId, foto);
                    return (
                      <li key={foto.id}>
                        <label
                          className={
                            checked
                              ? "block cursor-pointer overflow-hidden rounded-lg border-2 border-accent bg-card"
                              : "block cursor-pointer overflow-hidden rounded-lg border border-separator bg-card hover:border-label-tertiary"
                          }
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={checked}
                            onChange={() => toggleFoto(foto.id)}
                          />
                          <img
                            src={preview}
                            alt={foto.filename}
                            crossOrigin="use-credentials"
                            className="h-24 w-full object-cover"
                          />
                          <span className="block truncate px-2 py-1.5 text-xs text-label-secondary">
                            {foto.original_filename || foto.filename}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}
